import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import "./style.css";
import {
  HiArrowRight,
  HiOutlineCalendar,
  HiOutlineCash,
  HiOutlineCheckCircle,
  HiOutlineOfficeBuilding,
  HiOutlineShare,
  HiOutlineSparkles,
} from "react-icons/hi";
import Banner from "./Banner/index";
import CustomerCard from "../../components/CustomerCard/index";
import Howwork from "../../components/HowWorks/index";
import What from "../../components/What/index";
import { useContext } from "react";
import FeedbackContext from "../../context/cmscontext";
import { scrollTop, scrollToHash } from "../../utils/window";
import { getAuthCredits, getAuthToken } from "../../utils/userIdentity";

const trim = (str) => (str || "").trim();

function Landingmain(props) {
  const { navbarDimensions } = props;
  const location = useLocation();
  const history = useHistory();
  const { feedback } = useContext(FeedbackContext);
  const home_page = feedback.home_page;
  const auth = useSelector((state) => state.auth);
  const isLoggedIn = Boolean(getAuthToken(auth));
  const availableCredits = getAuthCredits(auth);

  useEffect(() => {
    if (location.pathname !== "/") return undefined;

    if (location.hash) {
      const timer = window.setTimeout(() => scrollToHash(location.hash), 100);
      return () => window.clearTimeout(timer);
    }

    scrollTop();
    return undefined;
  }, [location.pathname, location.hash]);

  const scrollToSell = () => {
    scrollToHash("#sell-car");
  };

  const weOffer = [
    {
      id: "sell",
      num: "01",
      title: trim(home_page.sell_your_car),
      description: trim(home_page.sell_your_car_desp),
      image: "/images/offer-sell-vehicle.jpg",
      cta: "Get your valuation",
      featured: true,
      onClick: scrollToSell,
    },
    {
      id: "buy",
      num: "02",
      title: trim(home_page.buy_car_heading),
      description: trim(home_page.buy_car_desp),
      image: "/images/offer-buy-vehicle.jpg",
      cta: "Browse inventory",
      onClick: () => history.push("/cars"),
    },
    {
      id: "exchange",
      num: "03",
      title: trim(home_page.exchange_car),
      description: trim(home_page.exchange_car_desp),
      image: "/images/offer-exchange-vehicle.jpg",
      comingSoon: true,
      soonLabel: trim(home_page.exchange_car_cooming_soon) || "Coming soon",
    },
  ];

  const creditPoints = [
    {
      title: "Credits land in wallets",
      body: "Successful referrals add credits to your account wallet, ready to use toward eligible AutoTrade Port services.",
      Icon: HiOutlineCash,
    },
    {
      title: "Client & dealer wallets",
      body: "Clients and dealers each keep their credits in the wallet linked to their account.",
      Icon: HiOutlineOfficeBuilding,
    },
    {
      title: "Sender and receiver earn",
      body: "When a referral succeeds, both the person sharing the link and the person joining receive credits.",
      Icon: HiOutlineShare,
    },
  ];

  const creditSteps = [
    {
      num: "1",
      title: "Get your link",
      body: "Sign in and copy your personal referral link from your account.",
    },
    {
      num: "2",
      title: "Share it",
      body: "Send it to friends, clients, or dealers who want to sell or buy.",
    },
    {
      num: "3",
      title: "Both earn credits",
      body: "Credits post to both wallets once the referral completes.",
    },
  ];

  const creditUses = [
    "Book inspection appointments for your valuation",
    "Track every credit in your account wallet",
    "Available for both client and dealer accounts",
  ];

  return (
    <div className="pi-page landingmain_main_cont">
      <Banner
        style={{
          width: "100%",
        }}
      />

      <section className="pi-offer" id="sell-buy">
        <div className="pi-offer__inner">
          <header className="pi-section-head">
            <p className="pi-label">Services</p>
            <h2>{trim(home_page.what_doest)}</h2>
            <p>
              One platform to buy, sell, and exchange new and used vehicles — built for owners and dealers worldwide.
            </p>
          </header>

          <div className="pi-offer__grid">
            {weOffer.map((item) => {
              const { id, num, title, description, image, cta, featured, comingSoon, soonLabel, onClick } = item;
              return (
                <button
                  key={id}
                  type="button"
                  className={[
                    "pi-offer__card",
                    featured ? "pi-offer__card--featured" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={comingSoon ? undefined : onClick}
                  disabled={comingSoon}
                  aria-disabled={comingSoon || undefined}
                >
                  {featured ? <span className="pi-offer__card-flag">Most popular</span> : null}
                  <div
                    className="pi-offer__card-bg"
                    style={{ backgroundImage: `url(${image})` }}
                    aria-hidden
                  />
                  <div className="pi-offer__card-shade" aria-hidden />
                  <div className="pi-offer__card-body">
                    <span className="pi-offer__card-num">{num}</span>
                    <h3>{title}</h3>
                    <p>{description}</p>
                    {comingSoon ? (
                      <span className="pi-offer__card-badge">{soonLabel}</span>
                    ) : (
                      <span className="pi-offer__card-action">
                        {cta}
                        <HiArrowRight aria-hidden />
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="pi-home-credits" id="credits">
        <div className="pi-home-credits__inner">
          <header className="pi-section-head">
            <p className="pi-label">Credits</p>
            <h2>Earn credits when you refer</h2>
            <p>
              Share AutoTrade Port with friends, clients, or dealers. When a
              referral succeeds, credits are added to both wallets.
            </p>
          </header>

          <div className="pi-referral-credits__grid">
            {creditPoints.map(({ title, body, Icon }) => (
              <article key={title} className="pi-referral-credits__card">
                <span className="pi-referral-credits__icon" aria-hidden="true">
                  <Icon size={22} />
                </span>
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>

          <div className="pi-home-credits__split">
            <div className="pi-home-credits__steps">
              <h3 className="pi-home-credits__subtitle">
                Three steps to earn credits
              </h3>
              <div className="pi-home-credits__timeline">
                {creditSteps.map(({ num, title, body }) => (
                  <div key={num} className="pi-home-credits__step">
                    <span className="pi-home-credits__step-num">{num}</span>
                    <div className="pi-home-credits__step-copy">
                      <p className="pi-home-credits__step-title">{title}</p>
                      <p className="pi-home-credits__step-text">{body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <aside className="pi-home-credits__panel">
              {isLoggedIn ? (
                <>
                  <p className="pi-home-credits__panel-label">Your wallet</p>
                  <p className="pi-home-credits__balance">
                    {availableCredits == null ? "—" : availableCredits}
                    <span>available credits</span>
                  </p>
                </>
              ) : (
                <>
                  <span className="pi-home-credits__panel-icon" aria-hidden="true">
                    <HiOutlineSparkles size={24} />
                  </span>
                  <p className="pi-home-credits__panel-label">Start earning</p>
                  <p className="pi-home-credits__panel-lead">
                    Create an account to get your referral link and collect
                    credits in your wallet.
                  </p>
                </>
              )}

              <ul className="pi-home-credits__uses">
                {creditUses.map((use) => (
                  <li key={use}>
                    <HiOutlineCheckCircle size={16} aria-hidden="true" />
                    <span>{use}</span>
                  </li>
                ))}
              </ul>

              <div className="pi-home-credits__actions">
                {isLoggedIn ? (
                  <>
                    <Link to="/credits" className="pi-home-credits__primary">
                      View credit info
                      <HiArrowRight aria-hidden="true" />
                    </Link>
                    <Link to="/referral" className="pi-home-credits__secondary">
                      Get referral link
                    </Link>
                  </>
                ) : (
                  <>
                    <Link to="/signup" className="pi-home-credits__primary">
                      Create free account
                      <HiArrowRight aria-hidden="true" />
                    </Link>
                    <Link
                      to="/dostprogram"
                      className="pi-home-credits__secondary"
                    >
                      How credits work
                    </Link>
                  </>
                )}
              </div>

              <button
                type="button"
                className="pi-home-credits__ghost"
                onClick={scrollToSell}
              >
                <HiOutlineCalendar size={16} aria-hidden="true" />
                Use credits on your next valuation
              </button>
            </aside>
          </div>
        </div>
      </section>

      <section className="pi-advantage">
        <div className="pi-advantage__inner">
          <What />
        </div>
      </section>

      <section className="pi-stories">
        <div className="pi-stories__fx" aria-hidden="true">
          <span
            className="pi-stories__photo pi-stories__photo--base"
            style={{
              backgroundImage: `url(${process.env.PUBLIC_URL}/images/testimonials-section-bg.jpg)`,
            }}
          />
          <span className="pi-stories__mesh" />
          <span className="pi-stories__aurora pi-stories__aurora--a" />
          <span className="pi-stories__aurora pi-stories__aurora--b" />
          <span className="pi-stories__aurora pi-stories__aurora--c" />
          <span className="pi-stories__orb pi-stories__orb--a" />
          <span className="pi-stories__orb pi-stories__orb--b" />
          <span className="pi-stories__rays" />
          <span className="pi-stories__bokeh" />
          <span className="pi-stories__beam" />
          <span className="pi-stories__shimmer" />
          <span className="pi-stories__grain" />
          <span className="pi-stories__vignette" />
          <span className="pi-stories__scrim" />
        </div>
        <CustomerCard />
      </section>

      <section className="pi-process" id="how_work">
        <Howwork />
      </section>
    </div>
  );
}

export default Landingmain;
